/**
 * BillingPage — Settings → Subscription & billing.
 *
 * Clarity-first (founder reads it in seconds): ONE hero summary at the top
 * (plan + state + the single call-to-action), then live usage vs. the plan's
 * limits. Everything secondary — comparing/switching plans, buying WhatsApp
 * credits, the Garment OS add-on — is folded into collapsed sections so the
 * page doesn't open as a wall of equal-weight cards. No prices on the plans —
 * V1 activation is sales-led ("talk to us"). All data comes from
 * `billing.overview` (no hard-coded limits). @see system-decisions §15.
 */
import { Alert } from "@flint/ui";
import { PLAN_BY_CODE } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../workspace/index.ts";
import { SettingsShell } from "../settings-shell.tsx";
import { BillingHero } from "./billing-hero.tsx";
import { UsageMeters } from "./usage-meters.tsx";
import { PlanLadder } from "./plan-ladder.tsx";
import { AddonsSection } from "./addons-section.tsx";
import { WaTopup, AiTopup } from "./wa-topup.tsx";
import { CreditUsage } from "./credit-usage.tsx";

const STATUS_LABEL: Record<string, string> = {
  trialing: "Free trial",
  activation_required: "Activation required",
  active: "Active",
  past_due: "Payment overdue",
  soft_suspended: "Read-only",
  hard_suspended: "Locked",
  trial_expired: "Trial expired",
  on_hold: "On hold",
  canceled: "Canceled",
};

export function BillingPage() {
  const overview = trpc.billing.overview.useQuery({});
  const data = overview.data;

  const planName = data ? (PLAN_BY_CODE[data.plan]?.name ?? data.plan) : "";
  const statusText = data ? (STATUS_LABEL[data.status] ?? data.status) : "";
  const description = data
    ? `${planName} plan · ${statusText}${
        data.status === "trialing" && data.trialDaysLeft != null
          ? ` · ${data.trialDaysLeft} day${data.trialDaysLeft === 1 ? "" : "s"} left`
          : ""
      }`
    : "Your current plan, usage, and what's available as you grow.";

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Subscription" }]}>
      <SettingsShell activeSection="subscription" pageTitle="Subscription" pageDescription={description}>
        {overview.error ? (
          <Alert intent="bad" title="Couldn't load billing" description={overview.error.message} />
        ) : !data ? (
          <p className="text-sm text-muted">Loading your plan…</p>
        ) : (
          <div className="flex flex-col gap-4">
            <BillingHero
              plan={data.plan}
              planName={planName}
              status={data.status}
              trialEndsOn={data.trialEndsOn}
              trialDaysLeft={data.trialDaysLeft}
              writeEnabled={data.writeEnabled}
              cancelledAt={data.cancelledAt}
              currentPeriodEndsOn={data.currentPeriodEndsOn}
            />
            <UsageMeters usage={data.usage} showAi={data.enabledAddons.includes("ai")} showWhatsapp={data.enabledAddons.includes("whatsapp")} />
            {/* Secondary surfaces — collapsed by default so the page reads as
                "where am I + am I near a limit" first. */}
            <PlanLadder plan={data.plan} status={data.status} />
            <WaTopup enabled={data.enabledAddons.includes("whatsapp")} />
            <AiTopup enabled={data.enabledAddons.includes("ai")} />
            <CreditUsage showAi={data.enabledAddons.includes("ai")} showWhatsapp={data.enabledAddons.includes("whatsapp")} />
            <AddonsSection
              enabledAddons={data.enabledAddons}
              writeEnabled={data.writeEnabled}
              planActive={data.status === "active"}
            />
          </div>
        )}
      </SettingsShell>
    </WorkspaceShell>
  );
}
